import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuid } from "uuid";

import { CLIENT_ID, HUDL_CLIENT_ID_DRAW, HUDL_ID_URL, LOGIN_API, REDIRECT_URI } from "../redux/constants";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { usePostUserMetricsMutation, userLoggedIn, userLoggedInSynergy } from "../redux/slices";
import CacheUtils from "../utils/cache-utils";
import { paramify } from "../utils/url-utils";

export const useLogin = () => {
  const [allowNoAccessScreen, setAllowNoAccessScreen] = useState(false);
  const [loginCodeFailed, setLoginCodeFailed] = useState(false);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [postUserMetrics] = usePostUserMetricsMutation();

  const readOnly = location.pathname.includes("readonly");

  useEffect(() => {
    const hashParams = new URLSearchParams(location.hash.substr(1));
    const accessTokenInUrl = hashParams.get("access_token");
    if (accessTokenInUrl) {
      if (!readOnly) {
        const sendBackToHref = CacheUtils.getUserRequestedHref();
        if (sendBackToHref) {
          navigate(sendBackToHref, { replace: true });
        } else {
          navigate({ pathname: "home", hash: "" }, { replace: true });
        }
      }
      CacheUtils.setAccessToken(accessTokenInUrl);
    }

    const synergyTokenInUrl = hashParams.get("synergy_token");
    const stateInUrl = hashParams.get("state");
    if (synergyTokenInUrl) {
      CacheUtils.setSynergyToken(synergyTokenInUrl);
      const synergyTokenExpiresIn = hashParams.get("expires_in") || "0";
      CacheUtils.setSynergyTokenExpiration(Math.floor(Date.now() / 1000) + parseInt(synergyTokenExpiresIn));
    }

    const synergyToken = CacheUtils.getSynergyToken();
    if (synergyToken && synergyToken.length > 0) {
      dispatch(userLoggedInSynergy());

      if (stateInUrl) {
        const state = JSON.parse(stateInUrl);
        if (state && state.playId) {
          navigate({ pathname: "home/draw", hash: `playId=${state.playId}&modal=synergy` }, { replace: true });
        }
      }
    }

    const hasValidLoginCode = (loginCode: string) => {
      try {
        const decodedLoginCode = parseInt(atob(loginCode), 10) || 0;
        if (!loginCode || Date.now() - decodedLoginCode > 3600000) {
          return false;
        }
        return true;
      } catch (e) {
        return false;
      }
    };

    const token = CacheUtils.getAccessToken();
    if (token && token.length > 0) {
      dispatch(userLoggedIn());
      postUserMetrics();

      // A logged in user might not have access.  Allow this screen to be shown now.
      setAllowNoAccessScreen(true);
    } else {
      const loginHref = location.pathname + location.search + location.hash;
      CacheUtils.saveUserRequestedHref(loginHref);
      const urlParams = new URLSearchParams(window.location.search);
      const loginMode = urlParams.get("login");

      if (loginMode === "classic") {
        // Get loginCode from query params.  It's a base64 encoded unix timestamp in ms that's good for 1hour.  Show an error if it's not there,  or if it's older than 1 hour.
        const loginCode = urlParams.get("loginCode") || "";
        if (loginCode) {
          if (!hasValidLoginCode(loginCode)) {
            urlParams.delete("loginCode");
            window.history.replaceState(
              {},
              window.document.title,
              `${window.location.pathname}?${urlParams.toString()}`
            );
            console.error("Invalid login code or expired.");

            setLoginCodeFailed(true);
            return;
          }
          window.location.href = `${LOGIN_API}/authorize?response_type=token&client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}`;
          return;
        }
      }

      const fmIdParams = paramify({
        redirect_url: REDIRECT_URI,
        client_id_fms: CLIENT_ID,
      });

      const fmIdUrl = new URL(LOGIN_API);
      fmIdUrl.pathname = "callback/hudl-auth";
      fmIdUrl.search = fmIdParams;

      const hudlIdParams = paramify({
        client_id: HUDL_CLIENT_ID_DRAW,
        redirect_uri: fmIdUrl.toString(),
        response_type: "code id_token",
        scope: "openid offline_access profile email",
        response_mode: "form_post",
        nonce: uuid(),
      });

      const hudlIdUrl = new URL(HUDL_ID_URL);
      hudlIdUrl.pathname = "authorize";
      hudlIdUrl.search = hudlIdParams;
      window.location.href = hudlIdUrl.toString();
    }
  }, [dispatch, postUserMetrics, location, navigate, readOnly]);

  const auth = useAppSelector((state) => state.auth);

  return { auth, allowNoAccessScreen, loginCodeFailed };
};
